import React, { useState, useMemo } from "react";
import "./TabCuantificaciones.css";
import ScrollDateInput from "../calendar/ScrollDateInput";

// ──────────────────────────────────────────
// SALARIOS MÍNIMOS HISTÓRICOS
// ──────────────────────────────────────────
const SMG = {
  2018: { general: 88.36,  frontera: 88.36  },
  2019: { general: 102.68, frontera: 176.72 },
  2020: { general: 123.22, frontera: 185.56 },
  2021: { general: 141.70, frontera: 213.39 },
  2022: { general: 172.87, frontera: 260.34 },
  2023: { general: 207.44, frontera: 312.41 },
  2024: { general: 248.93, frontera: 374.89 },
  2025: { general: 278.80, frontera: 419.88 },
  2026: { general: 278.80, frontera: 419.88 },
};

function obtenerSMG(anio, zona) {
  const keys = Object.keys(SMG).map(Number);
  const lo = Math.min(...keys), hi = Math.max(...keys);
  const y = anio < lo ? lo : anio > hi ? hi : anio;
  return SMG[y][zona];
}

// ──────────────────────────────────────────
// CÁLCULOS LABORALES
// ──────────────────────────────────────────
function calcDias(fi, fe) {
  const a = fi instanceof Date ? fi : new Date(String(fi).includes("T") ? fi : fi + "T00:00:00");
  const b = fe instanceof Date ? fe : new Date(String(fe).includes("T") ? fe : fe + "T00:00:00");
  return Math.floor((b - a) / 86400000) + 1;
}

function diasAnio(y) {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 366 : 365;
}

function diasVac(anios, anioEgreso) {
  const ley = anioEgreso >= 2023;
  const a = Math.floor(anios) || 1;
  if (!ley) {
    if (a === 1) return 6;  if (a === 2) return 8;  if (a === 3) return 10;
    if (a === 4) return 12; if (a <= 9) return 14;  if (a <= 14) return 16;
    if (a <= 19) return 18; if (a <= 24) return 20; if (a <= 29) return 22;
    return 24;
  }
  if (a === 1) return 12; if (a === 2) return 14; if (a === 3) return 16;
  if (a === 4) return 18; if (a === 5) return 20; if (a <= 10) return 22;
  if (a <= 15) return 24; if (a <= 20) return 26; if (a <= 25) return 28;
  if (a <= 30) return 30; return 32;
}

function calcSDI(sd, aniosS, anioEgreso) {
  const dv = diasVac(Math.floor(aniosS) || 1, anioEgreso);
  return sd * (1 + (15 + dv * 0.25) / 365);
}

function toDate(s) {
  if (s instanceof Date) return s;
  const str = String(s);
  return str.includes("T") ? new Date(str) : new Date(str + "T00:00:00");
}

function calcAguinaldo(sd, fi, fe) {
  const ing = toDate(fi), eg = toDate(fe);
  const ae = eg.getFullYear();
  const ini = ing.getFullYear() === ae ? ing : new Date(`${ae}-01-01T00:00:00`);
  const d = calcDias(ini, eg);
  const prop = (d / diasAnio(ae)) * 15;
  return { dias: prop, monto: prop * sd };
}

function calcVacaciones(sd, fi, fe) {
  const ing = toDate(fi), eg = toDate(fe);
  const tot = calcDias(ing, eg);
  const aniosS = tot / 365;
  const aniosC = Math.floor(aniosS);
  const aniversario = new Date(ing);
  aniversario.setFullYear(ing.getFullYear() + aniosC);
  const dUltimo = calcDias(aniversario, eg);
  const dvCorr = diasVac(aniosC + 1, eg.getFullYear());
  const prop = (dUltimo / 365) * dvCorr;
  return { diasTotales: prop, montoVac: prop * sd, montoPrima: prop * sd * 0.25 };
}

function calcPrima(aniosS, sd, anioEgreso, zona) {
  const smg = obtenerSMG(anioEgreso, zona);
  const tope = smg * 2;
  const base = Math.min(sd, tope);
  const dias = aniosS * 12;
  return { dias, salarioAplicado: base, topado: sd > tope, monto: dias * base };
}

function calcCaidos(fe, fProy, sdi) {
  const eg = toDate(fe), hasta = fProy ? toDate(fProy) : new Date();
  const trans = calcDias(eg, hasta);
  let diasC = 0, mesesI = 0;
  if (trans <= 0) { diasC = 0; mesesI = 0; }
  else if (trans <= 360) { diasC = trans; mesesI = 0; }
  else { diasC = 360; mesesI = (trans - 360) / 30; }
  const montoCaidos = diasC * sdi;
  const montoIntereses = 450 * sdi * 0.02 * mesesI;
  return { diasC, mesesI, montoCaidos, montoIntereses, total: montoCaidos + montoIntereses };
}

function ejecutarCalculo(sd, sdiBase, fi, fe, fProy, zona, jornadaH, hs, sr, procede90, procede20, procedePrima, procedeCaidos) {
  if (!sd || sd <= 0 || !fi || !fe) return null;
  const ing = new Date(fi + "T00:00:00"), eg = new Date(fe + "T00:00:00");
  if (eg < ing) return null;
  const anioEg = eg.getFullYear();
  const diasT = calcDias(ing, eg);
  const aniosS = diasT / 365;
  const sdi = sdiBase > 0 ? sdiBase : calcSDI(sd, aniosS, anioEg);

  const montoInd  = procede90    ? sdi * 90 : 0;
  const v20       = procede20    ? { dias: aniosS * 20, monto: aniosS * 20 * sdi } : { dias: 0, monto: 0 };
  const prima     = procedePrima ? calcPrima(aniosS, sd, anioEg, zona)             : { dias: 0, monto: 0, salarioAplicado: 0, topado: false };
  const aguinaldo = calcAguinaldo(sd, fi, fe);
  const vacas     = calcVacaciones(sd, fi, fe);
  const caidos    = procedeCaidos ? calcCaidos(fe, fProy, sdi)                      : { diasC: 0, mesesI: 0, montoCaidos: 0, montoIntereses: 0, total: 0 };

  const horasD = Math.min(parseFloat(hs) || 0, 9);
  const horasT = Math.max(0, (parseFloat(hs) || 0) - 9);
  const salH   = sd / jornadaH;
  const semRec = parseFloat(sr) || 0;
  const montoHE = ((horasD * salH * 2) + (horasT * salH * 3)) * semRec;

  let total = aguinaldo.monto + vacas.montoVac + vacas.montoPrima;
  if (procede90)    total += montoInd;
  if (procede20)    total += v20.monto;
  if (procedePrima) total += prima.monto;
  if (procedeCaidos)total += caidos.total;
  if (montoHE > 0)  total += montoHE;

  const aniosE = Math.floor(aniosS);
  const mesesE = Math.floor((diasT % 365) / 30);
  const diasE  = Math.floor((diasT % 365) % 30);

  return { sdi, montoInd, v20, prima, aguinaldo, vacas, caidos, horasD, horasT, semRec, montoHE, total, aniosE, mesesE, diasE };
}

// ──────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────
const safeObj = (v) => (v && typeof v === "object" ? v : {});

function mapAccion(str) {
  const s = (str || "").toUpperCase();
  if (s.includes("REINSTALACION") || s.includes("REINSTALACIÓN")) return "despido_reinstalacion";
  if (s.includes("INDEMNIZACION") || s.includes("INDEMNIZACIÓN"))  return "despido_indemnizacion";
  if (s.includes("RESCISION")     || s.includes("RESCISIÓN"))      return "rescision_trabajador";
  if (s.includes("RENUNCIA"))                                       return "renuncia";
  if (s.includes("JUSTIFICADO"))                                    return "despido_justificado";
  if (s.includes("TERMINACION")   || s.includes("TERMINACIÓN") || s.includes("VOLUNTARIA")) return "terminacion_voluntaria";
  return "terminacion_voluntaria";
}

function inferJornada(txt) {
  if (!txt) return 8;
  const t = txt.toLowerCase();
  if (t.includes("nocturna")) return 7;
  if (t.includes("mixta"))    return 7.5;
  const m = t.match(/(\d{1,2}):00\s+a\s+(\d{1,2}):00/);
  if (m) {
    const diff = parseInt(m[2]) - parseInt(m[1]);
    if (diff >= 6 && diff <= 8) return diff;
  }
  return 8;
}

function isoToInput(iso) {
  if (!iso) return "";
  return iso.split("T")[0];
}

const fmt = (v) => {
  const n = Number(v) || 0;
  return `$ ${n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtHoy = () => new Date().toLocaleDateString("es-MX");

// ──────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────
export default function TabCuantificaciones({ detalle = null }) {
  const d    = safeObj(detalle);
  const gen  = safeObj(d.datos_generales);
  const cond = safeObj(d.condiciones_trabajo);
  const acc  = safeObj(d.acciones_prestaciones);

  // Datos del expediente (solo para mostrar en el recibo)
  const actorName  = acc.trabajador_nombre  || gen.nombre_parte_actora || d.nombre_actora  || "-";
  const demandado  = gen.empresa_nombre     || d.nombre_empresa        || "-";
  const expediente = gen.numero_expediente  || d.expediente            || "-";
  const autoridad  = gen.nombre_autoridad   || d.nombre_autoridad      || "-";

  // Valores iniciales desde el detalle
  const fiInicial   = isoToInput(cond.fecha_ingreso);
  const feInicial   = isoToInput(cond.fecha_ultimo_dia_laborado);
  const salarioRec  = parseFloat(cond.salario_diario) || 0;
  const sdiBackend  = parseFloat(cond.salario_diario_integrado) || 0;
  const jornadaInf  = inferJornada(cond.jornada);
  const accionInf   = mapAccion(acc.accion_intentada);

  const hoy = new Date().toISOString().split("T")[0];

  // ── Estado del formulario ──
  const [fi,           setFi]           = useState(fiInicial);
  const [fe,           setFe]           = useState(feInicial);
  const [salManual,    setSalManual]     = useState(salarioRec > 0 ? String(salarioRec) : "");
  const [sdiManualOn,  setSdiManualOn]   = useState(false);
  const [sdiManualVal, setSdiManualVal]  = useState(sdiBackend > 0 ? String(sdiBackend) : "");
  const [tipoAccion,   setTipoAccion]   = useState(accionInf);
  const [jornadaSel,   setJornadaSel]   = useState(String(jornadaInf));
  const [zona,         setZona]         = useState("general");
  const [fProy,        setFProy]        = useState(hoy);
  const [horasSemana,  setHorasSemana]  = useState("");
  const [semanasRec,   setSemanasRec]   = useState("");
  const [calcAltOn,    setCalcAltOn]    = useState(false);
  const [salReclamado, setSalReclamado] = useState("");
  const [sdiReclamado, setSdiReclamado] = useState("");

  const effectiveSal    = parseFloat(salManual) || 0;
  const effectiveSdi    = sdiManualOn ? (parseFloat(sdiManualVal) || 0) : 0;
  const effectiveJornada = parseFloat(jornadaSel) || 8;

  // Reglas según acción seleccionada
  const procede90     = ["terminacion_voluntaria","despido_indemnizacion","despido_reinstalacion","rescision_trabajador"].includes(tipoAccion);
  const procede20     = ["terminacion_voluntaria","despido_reinstalacion","rescision_trabajador"].includes(tipoAccion);
  const procedePrima  = tipoAccion !== "renuncia";
  const procedeCaidos = ["despido_indemnizacion","despido_reinstalacion","rescision_trabajador"].includes(tipoAccion);
  const procedeNeg    = tipoAccion === "terminacion_voluntaria";

  const calc = useMemo(() =>
    ejecutarCalculo(effectiveSal, effectiveSdi, fi, fe, fProy, zona, effectiveJornada, horasSemana, semanasRec, procede90, procede20, procedePrima, procedeCaidos),
    [effectiveSal, effectiveSdi, fi, fe, fProy, zona, effectiveJornada, horasSemana, semanasRec, procede90, procede20, procedePrima, procedeCaidos]
  );

  const calcAlt = useMemo(() => {
    if (!calcAltOn) return null;
    const salAlt = parseFloat(salReclamado) || 0;
    const sdiAlt = parseFloat(sdiReclamado) || 0;
    return ejecutarCalculo(salAlt, sdiAlt, fi, fe, fProy, zona, effectiveJornada, horasSemana, semanasRec, procede90, procede20, procedePrima, procedeCaidos);
  }, [calcAltOn, salReclamado, sdiReclamado, fi, fe, fProy, zona, effectiveJornada, horasSemana, semanasRec, procede90, procede20, procedePrima, procedeCaidos]);

  const mostrarHE = (parseFloat(horasSemana) > 0) && (parseFloat(semanasRec) > 0);

  return (
    <div className="cq-wrap">

      {/* ══════════════ LEFT: FORMULARIO ══════════════ */}
      <main className="cq-left">

        {/* SECCIÓN 0 — Fechas y Salario */}
        <section className="cq-section">
          <h2 className="cq-section-h">0. Fechas y salario de la relación laboral</h2>
          <div className="cq-grid">
            <div className="cq-field">
              <label>Fecha de Ingreso</label>
              <ScrollDateInput style={{ width: "100%" }} value={fi} onChange={setFi} />
            </div>
            <div className="cq-field">
              <label>Fecha Último Día Laborado</label>
              <ScrollDateInput style={{ width: "100%" }} value={fe} onChange={setFe} />
            </div>
            <div className="cq-field">
              <label>Salario Diario (MXN)</label>
              <div className="cq-input-icon">
                <span>$</span>
                <input type="number" step="0.01" min="0" placeholder="0.00"
                  value={salManual} onChange={(e) => setSalManual(e.target.value)} />
              </div>
            </div>
            <div className="cq-field">
              <label>SDI <span className="cq-muted">(se calcula si se deja vacío)</span></label>
              <div className="cq-input-icon">
                <span>$</span>
                <input type="number" step="0.01" min="0" placeholder="0.00"
                  value={sdiManualVal} onChange={(e) => setSdiManualVal(e.target.value)}
                  disabled={!sdiManualOn} />
              </div>
            </div>
          </div>

          <div className="cq-toggle-row" style={{ marginTop: "1.25rem" }}>
            <label className="cq-toggle">
              <input type="checkbox" checked={sdiManualOn}
                onChange={(e) => { setSdiManualOn(e.target.checked); if (!e.target.checked) setSdiManualVal(""); }} />
              <span className="cq-slider" />
            </label>
            <span className="cq-toggle-label">Ingresar Salario Diario Integrado (SDI) manualmente</span>
          </div>
        </section>

        {/* SECCIÓN 1 — Acción y Jornada */}
        <section className="cq-section">
          <h2 className="cq-section-h">1. Acción Demandada y Jornada</h2>
          <div className="cq-grid">
            <div className="cq-field cq-full">
              <label>Acción Demandada / Tipo de Terminación</label>
              <select value={tipoAccion} onChange={(e) => setTipoAccion(e.target.value)}>
                <option value="terminacion_voluntaria">Terminación Voluntaria (Para Negociar)</option>
                <option value="renuncia">Renuncia Voluntaria</option>
                <option value="despido_indemnizacion">Despido Injustificado (Indemnización Const.)</option>
                <option value="despido_reinstalacion">Despido Injustificado (Reinstalación)</option>
                <option value="rescision_trabajador">Rescisión de Contrato (Imputable al Patrón)</option>
                <option value="despido_justificado">Despido Justificado (Imputable al Trabajador)</option>
              </select>
            </div>
            <div className="cq-field">
              <label>Tipo de Jornada</label>
              <select value={jornadaSel} onChange={(e) => setJornadaSel(e.target.value)}>
                <option value="8">Diurna (8 hrs)</option>
                <option value="7.5">Mixta (7.5 hrs)</option>
                <option value="7">Nocturna (7 hrs)</option>
              </select>
            </div>
          </div>
        </section>

        {/* SECCIÓN 2 — Zona y Proyección */}
        <section className="cq-section">
          <h2 className="cq-section-h">2. Zona y Proyección</h2>
          <div className="cq-grid">
            <div className="cq-field">
              <label>Zona Geográfica (SMG)</label>
              <select value={zona} onChange={(e) => setZona(e.target.value)}>
                <option value="general">Resto del País (General)</option>
                <option value="frontera">Frontera Norte</option>
              </select>
            </div>
            <div className="cq-field">
              <label>Proyectar cálculo a fecha <span className="cq-muted">(Actual por defecto)</span></label>
              <ScrollDateInput style={{ width: "100%" }} value={fProy} onChange={setFProy} />
              {procedeCaidos && <small className="cq-hint">Usado para límite de salarios caídos.</small>}
            </div>
          </div>
        </section>

        {/* SECCIÓN 3 — Horas Extras */}
        <section className="cq-section">
          <h2 className="cq-section-h">3. Horas Extras</h2>
          <div className="cq-grid">
            <div className="cq-field">
              <label>Horas Extras a la Semana</label>
              <input type="number" min="0" placeholder="0"
                value={horasSemana} onChange={(e) => setHorasSemana(e.target.value)} />
            </div>
            <div className="cq-field">
              <label>Semanas Reclamadas</label>
              <input type="number" min="0" placeholder="0"
                value={semanasRec} onChange={(e) => setSemanasRec(e.target.value)} />
            </div>
          </div>
        </section>

        {/* SECCIÓN 4 — Cálculo Alternativo */}
        <section className="cq-section">
          <div className="cq-toggle-row">
            <label className="cq-toggle">
              <input type="checkbox" checked={calcAltOn}
                onChange={(e) => { setCalcAltOn(e.target.checked); if (!e.target.checked) { setSalReclamado(""); setSdiReclamado(""); } }} />
              <span className="cq-slider" />
            </label>
            <span className="cq-toggle-label cq-toggle-accent">Habilitar Cálculo Alternativo (Salario Reclamado)</span>
          </div>
          {calcAltOn && (
            <div className="cq-grid cq-alt-box">
              <div className="cq-field">
                <label>Salario Diario Reclamado (MXN)</label>
                <div className="cq-input-icon">
                  <span>$</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00"
                    value={salReclamado} onChange={(e) => setSalReclamado(e.target.value)} />
                </div>
              </div>
              <div className="cq-field">
                <label>SDI Reclamado Manual <span className="cq-muted">(Opcional)</span></label>
                <div className="cq-input-icon">
                  <span>$</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00"
                    value={sdiReclamado} onChange={(e) => setSdiReclamado(e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </section>

        {/* NOTA INFORMATIVA */}
        <section className="cq-info">
          <p>Las vacaciones cambian dependiendo el año de egreso (antes o después de Vacaciones Dignas 2023). La Prima de Antigüedad considera el tope histórico de 2 SMG de la zona seleccionada.</p>
        </section>

      </main>

      {/* ══════════════ RIGHT: RECIBO ══════════════ */}
      <aside className="cq-right">
        <div className="cq-receipt">

          {/* MEMBRETE */}
          <div className="cq-membrete">
            <div className="cq-mbr-title">
              <h1>ONTIVEROS &amp; ASOCIADOS</h1>
              <h2>ABOGADOS</h2>
            </div>
            <div className="cq-mbr-sep" />
            <div className="cq-mbr-contact">
              <div className="cq-mbr-col">
                <p>Calle "H" y Ave. Marmoleros # 1598 Altos</p>
                <p>Tel. 686 5 54 48 52.</p>
                <p>Col. Industrial, Mexicali Baja California C.P. 21010</p>
                <p>Correo Electrónico: <u>recepcionmxl@ontiverosyasociados.com.mx</u></p>
              </div>
              <div className="cq-mbr-col">
                <p>Calle 2 número 200 local 8, Plaza Gabriela, Comercial 83449</p>
                <p>Tels. 653 536-72-02, 653-515-19-90</p>
                <p>San Luis Río Colorado, Sonora</p>
                <p>Correo Electrónico: <u>victor@ontiverosyasociados.com.mx</u></p>
              </div>
            </div>
          </div>

          {/* HEADER DEL RECIBO */}
          <div className="cq-rh">
            <h2 className="cq-rh-title">CUANTIFICACIÓN</h2>
            <div className="cq-rh-names">
              <div>Actor: <strong>{actorName}</strong></div>
              <div>Demandado: <strong>{demandado}</strong></div>
            </div>
            <div className="cq-rh-juicio">
              <span>Exp: {expediente}</span> | <span>{autoridad}</span>
            </div>
            <div className="cq-rh-meta">
              <span>Fecha: {fmtHoy()}</span>
              <span>
                {calc
                  ? `${calc.aniosE} años, ${calc.mesesE} meses, ${calc.diasE} días`
                  : "0 años, 0 meses, 0 días"}
              </span>
              <div className="cq-badges">
                <span className="cq-badge">SDI: {calc ? fmt(calc.sdi) : "$0.00"}</span>
                {calcAltOn && calcAlt && (
                  <span className="cq-badge cq-badge-alt">SDI Rec: {fmt(calcAlt.sdi)}</span>
                )}
              </div>
            </div>
          </div>

          {/* CABECERA DE COLUMNAS */}
          {calcAltOn && (
            <div className="cq-cols-hdr">
              <div>Reconocido</div>
              <div>Reclamado</div>
            </div>
          )}

          {/* CUERPO */}
          <div className="cq-rbody">

            {/* INDEMNIZACIONES */}
            {(procede90 || procede20 || procedePrima) && (
              <div className="cq-rsection">
                <h3>Indemnizaciones</h3>

                {procede90 && (
                  <div className="cq-rrow">
                    <div className="cq-rlabel">
                      <span>Indemnización Constitucional</span>
                      <small>90 días de SDI</small>
                    </div>
                    <div className="cq-rvals">
                      <span className="cq-val-main">{fmt(calc?.montoInd ?? 0)}</span>
                      {calcAltOn && <span className="cq-val-alt">{fmt(calcAlt?.montoInd ?? 0)}</span>}
                    </div>
                  </div>
                )}

                {procede20 && (
                  <div className="cq-rrow">
                    <div className="cq-rlabel">
                      <span>Indemnización 20 Días / Año</span>
                      <small>
                        {calc ? `${calc.v20.dias.toFixed(2)} días (proporcional)` : "20 días x Año de Servicio (Proporcional)"}
                      </small>
                    </div>
                    <div className="cq-rvals">
                      <span className="cq-val-main">{fmt(calc?.v20.monto ?? 0)}</span>
                      {calcAltOn && <span className="cq-val-alt">{fmt(calcAlt?.v20.monto ?? 0)}</span>}
                    </div>
                  </div>
                )}

                {procedePrima && (
                  <div className="cq-rrow">
                    <div className="cq-rlabel">
                      <span>Prima de Antigüedad</span>
                      <small>
                        {calc
                          ? `${calc.prima.dias.toFixed(2)} días x ${fmt(calc.prima.salarioAplicado)}${calc.prima.topado ? " (Topado a 2 SMG)" : ""}`
                          : "12 días por año"}
                      </small>
                    </div>
                    <div className="cq-rvals">
                      <span className="cq-val-main">{fmt(calc?.prima.monto ?? 0)}</span>
                      {calcAltOn && <span className="cq-val-alt">{fmt(calcAlt?.prima.monto ?? 0)}</span>}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* PRESTACIONES (FINIQUITO) */}
            <div className="cq-rsection">
              <h3>Prestaciones (Finiquito)</h3>

              <div className="cq-rrow">
                <div className="cq-rlabel">
                  <span>Aguinaldo Proporcional</span>
                  <small>{calc ? `${calc.aguinaldo.dias.toFixed(2)} días proporcionales` : "0 días"}</small>
                </div>
                <div className="cq-rvals">
                  <span className="cq-val-main">{fmt(calc?.aguinaldo.monto ?? 0)}</span>
                  {calcAltOn && <span className="cq-val-alt">{fmt(calcAlt?.aguinaldo.monto ?? 0)}</span>}
                </div>
              </div>

              <div className="cq-rrow">
                <div className="cq-rlabel">
                  <span>Vacaciones Proporcionales</span>
                  <small>{calc ? `${calc.vacas.diasTotales.toFixed(2)} días proporcionales` : "0 días"}</small>
                </div>
                <div className="cq-rvals">
                  <span className="cq-val-main">{fmt(calc?.vacas.montoVac ?? 0)}</span>
                  {calcAltOn && <span className="cq-val-alt">{fmt(calcAlt?.vacas.montoVac ?? 0)}</span>}
                </div>
              </div>

              <div className="cq-rrow">
                <div className="cq-rlabel">
                  <span>Prima Vacacional</span>
                  <small>25% s/ Vacaciones</small>
                </div>
                <div className="cq-rvals">
                  <span className="cq-val-main">{fmt(calc?.vacas.montoPrima ?? 0)}</span>
                  {calcAltOn && <span className="cq-val-alt">{fmt(calcAlt?.vacas.montoPrima ?? 0)}</span>}
                </div>
              </div>

              {mostrarHE && (
                <div className="cq-rrow">
                  <div className="cq-rlabel">
                    <span>Horas Extras</span>
                    <small>
                      {calc
                        ? `${calc.horasD} dobles, ${calc.horasT} triples x ${calc.semRec} sem.`
                        : "0 dobles, 0 triples"}
                    </small>
                  </div>
                  <div className="cq-rvals">
                    <span className="cq-val-main">{fmt(calc?.montoHE ?? 0)}</span>
                    {calcAltOn && <span className="cq-val-alt">{fmt(calcAlt?.montoHE ?? 0)}</span>}
                  </div>
                </div>
              )}
            </div>

            {/* SALARIOS CAÍDOS E INTERESES */}
            {procedeCaidos && (
              <div className="cq-rsection">
                <h3>Salarios Caídos e Intereses</h3>

                <div className="cq-rrow">
                  <div className="cq-rlabel">
                    <span>Salarios Caídos (Máx 12 meses)</span>
                    <small>{calc ? `${calc.caidos.diasC} días transcurridos` : "0 días"}</small>
                  </div>
                  <div className="cq-rvals">
                    <span className="cq-val-main">{fmt(calc?.caidos.montoCaidos ?? 0)}</span>
                    {calcAltOn && <span className="cq-val-alt">{fmt(calcAlt?.caidos.montoCaidos ?? 0)}</span>}
                  </div>
                </div>

                <div className="cq-rrow">
                  <div className="cq-rlabel">
                    <span>Intereses (2% mensual)</span>
                    <small>
                      {calc
                        ? `${calc.caidos.mesesI.toFixed(2)} meses s/ 15 meses de SDI`
                        : "0 meses s/ 15 meses de SDI"}
                    </small>
                  </div>
                  <div className="cq-rvals">
                    <span className="cq-val-main">{fmt(calc?.caidos.montoIntereses ?? 0)}</span>
                    {calcAltOn && <span className="cq-val-alt">{fmt(calcAlt?.caidos.montoIntereses ?? 0)}</span>}
                  </div>
                </div>
              </div>
            )}

            {/* ESCENARIOS DE NEGOCIACIÓN */}
            {procedeNeg && calc && (
              <div className="cq-rsection">
                <h3>Escenarios de Negociación</h3>
                {(() => {
                  const finiq = calc.aguinaldo.monto + calc.vacas.montoVac + calc.vacas.montoPrima;
                  const prim  = calc.prima.monto;
                  const sdi   = calc.sdi;
                  return [
                    { label: "Finiquito",              val: finiq },
                    { label: "+ Prima de Antigüedad",  val: finiq + prim },
                    { label: "+ 15 días",              val: finiq + prim + sdi * 15 },
                    { label: "+ 30 días",              val: finiq + prim + sdi * 30 },
                    { label: "+ 45 días",              val: finiq + prim + sdi * 45 },
                    { label: "+ 60 días",              val: finiq + prim + sdi * 60 },
                    { label: "+ 75 días",              val: finiq + prim + sdi * 75 },
                    { label: "+ 90 días",              val: finiq + prim + sdi * 90 },
                  ].map((r, i) => (
                    <div className="cq-rrow" key={i}>
                      <div className="cq-rlabel"><span>{r.label}</span></div>
                      <div className="cq-rvals"><span className="cq-val-main">{fmt(r.val)}</span></div>
                    </div>
                  ));
                })()}
              </div>
            )}

          </div>

          {/* FOOTER DEL RECIBO */}
          <div className="cq-rfooter">
            <div className="cq-total-row">
              <span>Total Proyectado</span>
              <div className="cq-total-vals">
                <span className="cq-grand">{fmt(calc?.total ?? 0)}</span>
                {calcAltOn && calcAlt && (
                  <span className="cq-alt-grand">{fmt(calcAlt?.total ?? 0)}</span>
                )}
              </div>
            </div>
            <button className="cq-btn-print" type="button" onClick={() => window.print()}>
              Imprimir / Guardar PDF
            </button>
          </div>

        </div>
      </aside>

    </div>
  );
}
